import { formatLocal } from '@cis/shared';
import {
  taskContentSchema,
  type TaskContent,
  type TaskDetail,
  type TaskStep,
} from '@cis/api-client';
import { StepAssignees } from './step-assign.tsx';
import type { TaskWriter } from './use-task-writer.ts';

// A task with no runbook in its pack still shows its description and steps.
const NO_RUNBOOK: NonNullable<TaskContent['runbook']> = {
  goal: '',
  beforeYouStart: [],
  doneWhen: [],
  pitfalls: [],
  escalateIf: [],
  rollback: '',
};

function TextList(props: { readonly title: string; readonly items: readonly string[] }) {
  const items = props.items.filter((i) => i.trim() !== '');
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="field__label">{props.title}</h3>
      <ul className="plain-list">
        {items.map((item, n) => (
          <li key={`${String(n)}-${item}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function Prose(props: { readonly title: string; readonly text: string | null }) {
  if (props.text === null || props.text.trim() === '') return null;
  return (
    <div>
      <h3 className="field__label">{props.title}</h3>
      <p className="step__text">{props.text}</p>
    </div>
  );
}

function TickNote(props: { readonly step: TaskStep }): React.JSX.Element | null {
  const tick = props.step.tick;
  if (tick === null) return null;
  return (
    <p className="muted">
      Ticked by {tick.by.displayName} on {formatLocal(tick.at)}.
      {tick.textChanged && (
        <strong className="step__changed"> The step text changed after this tick.</strong>
      )}
    </p>
  );
}

function Step(props: {
  readonly step: TaskStep;
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
  readonly readOnly: boolean;
  readonly canAssign: boolean;
}): React.JSX.Element {
  const { step, writer, readOnly } = props;
  const ticked = step.tick !== null;
  const disabled = readOnly || !step.canTick || writer.pending;
  return (
    <li className="step">
      <label className="step__head">
        <input
          type="checkbox"
          checked={ticked}
          disabled={disabled}
          aria-describedby={`step-${String(step.no)}-do`}
          onChange={(e) =>
            void writer.send({ kind: 'tick', stepNo: step.no, ticked: e.target.checked })
          }
        />
        <span>
          Step {step.no}
          {step.required ? '' : ' (optional)'}
        </span>
      </label>
      <p className="step__text" id={`step-${String(step.no)}-do`}>
        {step.do}
      </p>
      <Prose title="How" text={step.how} />
      <Prose title="Check" text={step.check} />
      {step.who !== null && step.who !== '' && <p className="muted">Who: {step.who}</p>}
      <TickNote step={step} />
      <StepAssignees
        step={step}
        detail={props.detail}
        writer={writer}
        canAssign={!readOnly && props.canAssign}
      />
    </li>
  );
}

/** The runbook (spec F4): goal, before you start, the steps with server-side ticks, done when. */
export function Runbook(props: {
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
  readonly readOnly: boolean;
}): React.JSX.Element {
  const { detail } = props;
  const content = taskContentSchema.parse(detail.content);
  const rb = content.runbook ?? NO_RUNBOOK;
  return (
    <section className="card" aria-labelledby="runbook-title">
      <h2 className="card__title" id="runbook-title">
        Runbook
      </h2>
      <Prose title="What this task is" text={content.description} />
      <Prose title="Goal" text={rb.goal} />
      <TextList title="Before you start" items={rb.beforeYouStart} />
      <ol className="steps" aria-label="Steps">
        {detail.steps.map((step) => (
          <Step
            key={step.no}
            step={step}
            detail={detail}
            writer={props.writer}
            readOnly={props.readOnly}
            canAssign={detail.permissions.assign}
          />
        ))}
      </ol>
      <TextList title="Done when" items={rb.doneWhen} />
      <TextList title="Watch out for" items={rb.pitfalls} />
      <TextList title="Ask Arturo if" items={rb.escalateIf} />
      <Prose title="If you need to undo it" text={rb.rollback} />
    </section>
  );
}
